import _ from "lodash";
import React from "react";
import { useI18n } from "Hooks";

// design system
import { Checkbox } from "@toddle-edu/ds-web";

// third party
import classNames from "classnames";

// utils
import { onEnterOrSpaceKeyDown } from "Utils";

// scss
import classes from "./ListSelection.scss";

const ALL_OPTION = {
  id: "all",
  label: "common:all",
  isLocalised: false,
};

const getOptions = options => {
  return _.map(options, option => {
    const { id, key, value } = option;

    return {
      ...option,
      id: id || key || value,
    };
  });
};

const getIsEveryOptionSelected = (value, options) => {
  return _.every(options, option => _.includes(value, option?.id));
};

const getIsSomeOptionsSelected = (value, options) => {
  return (
    _.some(options, option => _.includes(value, option?.id)) &&
    !getIsEveryOptionSelected(value, options)
  );
};

const ListSelection = props => {
  const {
    value,
    fieldKey,
    options: _options,
    updateInputField,
    multi = false,
    isValueArray = false,
  } = props;

  const { t } = useI18n();

  // ----------------------------------------------------------------- //

  const options = getOptions(_options);
  const allOptions = multi ? [ALL_OPTION, ...options] : options;

  const isEveryOptionSelected = getIsEveryOptionSelected(value, options);
  const isSomeOptionsSelected = multi
    ? getIsSomeOptionsSelected(value, options)
    : false;

  // ----------------------------------------------------------------- //

  const onClickOption = id => {
    const isAllSelected = id === ALL_OPTION.id;
    const isOptionAlreadySelected = _.includes(value, id);

    let updatedValue = null;

    if (multi) {
      updatedValue = isOptionAlreadySelected
        ? _.without(value, id)
        : [...value, id];
    } else {
      updatedValue = isOptionAlreadySelected ? null : id;

      if (updatedValue && isValueArray) {
        updatedValue = [updatedValue];
      }
    }

    if (fieldKey === "pedagogicalApproachDP") {
      updatedValue = _.map(options, ({ id, label }) => ({
        id,
        value: label,
        isSelected: isAllSelected
          ? !isEveryOptionSelected
          : _.includes(updatedValue, id),
      }));
    } else if (isAllSelected) {
      updatedValue = isEveryOptionSelected ? [] : _.map(options, "id");
    }

    if (fieldKey) {
      updateInputField({
        params: { [fieldKey]: updatedValue },
        extraParams: { nodes: options },
      });
    } else {
      updateInputField({
        params: updatedValue,
        extraParams: { nodes: options },
      });
    }
  };

  return (
    <div className={classes.container}>
      {_.map(allOptions, option => {
        const { id, label, isLocalised = true } = option || {};

        const isChecked =
          id === ALL_OPTION.id ? isEveryOptionSelected : _.includes(value, id);
        const isIndeterminate = id === ALL_OPTION.id && isSomeOptionsSelected;

        return (
          <div
            key={id}
            className={classes.listItem}
            onClick={() => onClickOption(id)}
            onKeyDown={e => onEnterOrSpaceKeyDown(e, () => onClickOption(id))}
            role={"button"}
            tabIndex={0}
          >
            <Checkbox
              dsVersion="2.0"
              isChecked={isChecked}
              isIndeterminate={isIndeterminate}
              testId={"assessment-aiContextBar-listSelection-checkbox"}
            />
            <span className={classNames(classes.listItemText, "text-body-l")}>
              {isLocalised ? label : t(label)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default ListSelection;
